import React, { useMemo } from 'react';
import { AgGridReact, CustomCellRendererProps } from 'ag-grid-react';
import { ColDef, themeAlpine } from 'ag-grid-community';
import { Tag } from '@wk/components-v3-react16';
import { convertTimeHumanReadable } from '../../../DateTimeCell/DateTimeCell';
import { Log } from '../LogType';
import CellLogMessage from './CellLogMessage/CellLogMessage';
import styles from './LogTable.module.scss';

const styled: React.CSSProperties = {
  fontFamily: 'Fira Sans,Helvetica Neue,Helvetica,Roboto,Arial,sans-serif',
};

interface LogTableProperties {
  logs: Log[];
  originalStartTime: string;
}

const LogTable = ({ logs, originalStartTime }: LogTableProperties) => {
  const columnDefinitions = useMemo<ColDef<Log>[]>(
    () => [
      {
        field: 'metadata.CSBWorkflowStepName',
        filter: 'agTextColumnFilter',
        filterParams: {
          buttons: ['cancel', 'reset', 'apply'],
          closeOnApply: true,
          maxNumConditions: 1,
          suppressAndOrCondition: true,
        },
        headerName: 'Step Name',
        valueGetter: (parameters) =>
          parameters.data?.metadata?.CSBWorkflowStepName ||
          'No Step name available',
      },
      {
        field: 'timestamp',
        filter: 'agDateColumnFilter',
        filterParams: {
          buttons: ['cancel', 'reset', 'apply'],
          closeOnApply: true,
          maxNumConditions: 1,
          suppressAndOrCondition: true,
        },
        headerName: 'Created time',
        maxWidth: 175,
        minWidth: 155,
        resizable: false,
        valueFormatter: (parameters) => {
          return convertTimeHumanReadable(parameters.value);
        },
      },
      {
        autoHeight: true,
        cellRenderer: (parameters: CustomCellRendererProps<Log>) => {
          if (!parameters.data) {
            return null;
          }
          return (
            <CellLogMessage
              data={parameters.data}
              rowIndex={parameters.node.rowIndex ?? 0}
              originalStartTime={originalStartTime}
            />
          );
        },
        field: 'message',
        filter: 'agTextColumnFilter',
        filterParams: {
          buttons: ['cancel', 'reset', 'apply'],
          closeOnApply: true,
          maxNumConditions: 1,
          suppressAndOrCondition: true,
        },
        flex: 3,
        headerName: 'Message',
        wrapText: true,
      },
      {
        cellRenderer: (parameters: CustomCellRendererProps<Log>) => {
          if (!parameters.value) {
            return null;
          }
          return (
            <Tag type="dark" flowClass={styles[`${parameters.value}`]}>
              {parameters.value}
            </Tag>
          );
        },
        field: 'logLevel',
        filter: 'agSetColumnFilter',
        filterParams: {
          buttons: ['cancel', 'reset', 'apply'],
          closeOnApply: true,
          maxNumConditions: 1,
          suppressAndOrCondition: true,
        },
        headerName: 'Severity',
        maxWidth: 120,
        minWidth: 120,
        resizable: false,
      },
    ],
    [originalStartTime]
  );

  const defaultColumnDefinition = useMemo<ColDef>(
    () => ({
      filter: true,
      flex: 1,
      resizable: true,
      sortable: true,
      suppressHeaderMenuButton: true,
    }),
    []
  );

  return (
    <div className={styles.container}>
      <div className={styles.gridStyle}>
        <AgGridReact
          theme={themeAlpine.withParams(styled)}
          rowData={logs}
          columnDefs={columnDefinitions}
          defaultColDef={defaultColumnDefinition}
          // rowHeight={80}
        />
      </div>
    </div>
  );
};

export default LogTable;
